import { memo, forwardRef, type ForwardedRef } from 'react';
import { BackSide, MathUtils, MeshBasicMaterial, CircleGeometry, type Group } from 'three';
import { type GroupProps } from '@react-three/fiber';
import Text from '@ubnt/ui-components/Text/Text';
import { DEVICE_SIZE } from 'modules/editor/constants';
import { Html } from 'modules/editor/components/Html';
import COLORS from 'styles/colors.module.scss';
import { DeviceShadow } from './DeviceShadow';
import { DeviceImageMaterial } from './DeviceImageMaterial';

import classes from './Device.module.scss';

export type DeviceProps = GroupProps & {
  imageUrl: string;
  deviceName?: string;
  signalPercentage?: number;
};

const commonMeshProps = {
  transparent: true,
  toneMapped: false,
  depthTest: false,
};
const segments = 36;
const size = DEVICE_SIZE;
const materialBlue = new MeshBasicMaterial({ color: COLORS.primary, side: BackSide, ...commonMeshProps });
const circleImage = new CircleGeometry(size, segments);
const zIndexRange: number[] = [];

const DeviceImpl = forwardRef((props: DeviceProps, ref: ForwardedRef<Group>) => {
  const { imageUrl, signalPercentage, deviceName, ...rest } = props;

  return (
    <group ref={ref} {...rest}>
      <DeviceShadow />

      {/* Signal indicator/border around device image */}
      {signalPercentage !== undefined && (
        <mesh rotation-y={Math.PI} material={materialBlue}>
          <circleGeometry
            args={[size * 1.1, segments, Math.PI / 2, MathUtils.degToRad((signalPercentage / 100) * 360)]}
          />
        </mesh>
      )}

      {/* Device image */}
      <mesh geometry={circleImage}>
        <DeviceImageMaterial imageUrl={imageUrl} {...commonMeshProps} />
      </mesh>

      {deviceName && (
        <Html zIndexRange={zIndexRange}>
          <div className={classes.deviceName}>
            <Text className={classes.deviceNameText} motif="dark" truncate>
              {deviceName}
            </Text>
          </div>
        </Html>
      )}
    </group>
  );
});

DeviceImpl.displayName = 'Device';

export const Device = memo(DeviceImpl);
